import { useEffect, useRef, useState, useCallback } from "react";
import { MessageSquare, Plus, Search, X, Send, User as UserIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BookingChat from "@/components/BookingChat";
import { toast } from "sonner";

function ReservationsInbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/conversations").then((r) => setThreads(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="grid md:grid-cols-[330px_1fr] gap-5">
      <div className="bg-white rounded-2xl shadow-card overflow-hidden max-h-[560px] overflow-y-auto">
        {threads.length === 0 ? (
          <p className="text-slate-400 p-6 text-sm">No conversations yet. Reservation guests appear here so you can reach out anytime.</p>
        ) : threads.map((t) => (
          <button key={t.booking_id} data-testid={`thread-${t.booking_id}`} onClick={() => setActive(t)}
            className={`w-full text-left px-5 py-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${active?.booking_id === t.booking_id ? "bg-blue-50" : ""}`}>
            <div className="flex justify-between items-center gap-2">
              <span className="font-medium text-slate-900 text-sm truncate">{t.customer_name || "Customer"}</span>
              <span className="text-slate-400 text-xs shrink-0">{t.has_messages && t.last_at ? new Date(t.last_at).toLocaleDateString() : ""}</span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5 truncate">{t.car_name}</p>
            <p className="text-slate-500 text-sm mt-1 truncate">{t.last_body ? `${t.last_role === "admin" ? "You: " : ""}${t.last_body}` : <span className="text-slate-300 italic">No messages yet — start the conversation</span>}</p>
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl shadow-card p-5">
        {active ? (
          <>
            <div className="mb-3">
              <h3 className="font-heading font-semibold">{active.customer_name}</h3>
              <p className="text-slate-400 text-sm">{active.car_name} · {active.pickup_date} → {active.return_date}</p>
            </div>
            <BookingChat bookingId={active.booking_id} role="admin" />
          </>
        ) : (
          <div className="grid place-items-center h-80 text-slate-400 text-sm">
            <div className="text-center"><MessageSquare className="mx-auto mb-2" size={28} /> Select a conversation</div>
          </div>
        )}
      </div>
    </div>
  );
}

function NewChatModal({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/users", { params: q ? { q } : {} }).then((r) => setUsers(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="new-chat-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold">New message</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2 mb-3">
          <Search size={15} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="new-chat-search" autoFocus placeholder="Search users by name or email…" className="flex-1 text-sm outline-none" />
        </div>
        <div className="max-h-72 overflow-y-auto -mx-2">
          {users.map((u) => (
            <button key={u.id} data-testid={`new-chat-user-${u.id}`} onClick={() => onPick(u)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 text-left">
              <span className="grid place-items-center w-8 h-8 rounded-full bg-slate-100 text-slate-500"><UserIcon size={15} /></span>
              <div><p className="text-sm font-medium text-slate-900">{u.name || "—"}</p><p className="text-xs text-slate-500">{u.email}</p></div>
            </button>
          ))}
          {users.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No users found.</p>}
        </div>
      </div>
    </div>
  );
}

function DirectInbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [picker, setPicker] = useState(false);
  const scroller = useRef(null);

  const loadThreads = useCallback(() => api.get("/admin/dm").then((r) => setThreads(r.data)).catch(() => {}), []);
  useEffect(() => { loadThreads(); const id = setInterval(loadThreads, 12000); return () => clearInterval(id); }, [loadThreads]);

  const loadMsgs = useCallback((uid) => api.get(`/admin/dm/${uid}`).then((r) => { setMsgs(r.data); setTimeout(() => scroller.current?.scrollTo(0, scroller.current.scrollHeight), 50); }).catch(() => {}), []);
  useEffect(() => { if (active) { loadMsgs(active.user_id); const id = setInterval(() => loadMsgs(active.user_id), 8000); return () => clearInterval(id); } }, [active, loadMsgs]);

  const send = async () => {
    const text = body.trim();
    if (!text || !active) return;
    setBody("");
    try { await api.post(`/admin/dm/${active.user_id}`, { body: text }); loadMsgs(active.user_id); loadThreads(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pick = (u) => { setPicker(false); const th = { user_id: u.id, name: u.name, email: u.email }; setThreads((p) => (p.some((t) => t.user_id === u.id) ? p : [th, ...p])); setActive(th); };

  return (
    <div className="grid md:grid-cols-[330px_1fr] gap-5">
      <div className="bg-white rounded-2xl shadow-card overflow-hidden max-h-[560px] flex flex-col">
        <button onClick={() => setPicker(true)} data-testid="new-chat-btn" className="flex items-center gap-2 m-3 px-4 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"><Plus size={16} /> New message</button>
        <div className="overflow-y-auto flex-1">
          {threads.length === 0 ? (
            <p className="text-slate-400 px-6 pb-6 text-sm">Start a direct chat with any registered user.</p>
          ) : threads.map((t) => (
            <button key={t.user_id} data-testid={`dm-thread-${t.user_id}`} onClick={() => setActive(t)}
              className={`w-full text-left px-5 py-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${active?.user_id === t.user_id ? "bg-blue-50" : ""}`}>
              <div className="flex justify-between items-center gap-2">
                <span className="font-medium text-slate-900 text-sm truncate">{t.name || t.email}</span>
                <span className="text-slate-400 text-xs shrink-0">{t.last_at ? new Date(t.last_at).toLocaleDateString() : ""}</span>
              </div>
              <p className="text-slate-500 text-sm mt-1 truncate">{t.last_body ? `${t.last_role === "admin" ? "You: " : ""}${t.last_body}` : <span className="text-slate-300 italic">Say hello…</span>}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-card p-5 flex flex-col">
        {active ? (
          <>
            <div className="mb-3 pb-3 border-b border-slate-100">
              <h3 className="font-heading font-semibold">{active.name || active.email}</h3>
              <p className="text-slate-400 text-sm">{active.email}</p>
            </div>
            <div ref={scroller} className="flex-1 max-h-[380px] overflow-y-auto space-y-2 mb-3" data-testid="dm-messages">
              {msgs.length === 0 && <p className="text-slate-300 text-sm text-center py-10">No messages yet.</p>}
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.sender_role === "admin" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                    {m.body}
                    <span className={`block text-[10px] mt-1 ${m.sender_role === "admin" ? "text-blue-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="dm-input" placeholder="Type a message…" className="belo-input flex-1" />
              <button onClick={send} data-testid="dm-send" className="grid place-items-center w-11 h-11 rounded-full bg-blue-600 text-white hover:bg-blue-700 shrink-0"><Send size={16} /></button>
            </div>
          </>
        ) : (
          <div className="grid place-items-center h-80 text-slate-400 text-sm">
            <div className="text-center"><MessageSquare className="mx-auto mb-2" size={28} /> Pick a chat or start a new one</div>
          </div>
        )}
      </div>
      {picker && <NewChatModal onClose={() => setPicker(false)} onPick={pick} />}
    </div>
  );
}

export function InboxPanel() {
  const [tab, setTab] = useState("reservations");
  return (
    <div className="mt-6" data-testid="inbox-panel">
      <div className="inline-flex items-center bg-slate-100 rounded-full p-1 mb-4">
        <button onClick={() => setTab("reservations")} data-testid="inbox-tab-reservations" className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "reservations" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Reservations</button>
        <button onClick={() => setTab("direct")} data-testid="inbox-tab-direct" className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "direct" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Direct messages</button>
      </div>
      {tab === "reservations" ? <ReservationsInbox /> : <DirectInbox />}
    </div>
  );
}
